import { cartLineId, withLineIds } from "../utils/variantCore";
import { createSlice } from "@reduxjs/toolkit";
import { withDealPricing, dealPricingFields } from "../utils/vendorDiscounts";
import { cartOutletId } from "../utils/vendorOutlets";
import {
  acceptQuotedPrice,
  applyQuote,
  branchProductChanged,
  lineFieldsFor,
  markLinesChanged,
  markLinesForMove,
  markLinesUnchecked,
  oneLocationCart,
  withoutBranchRead,
} from "../utils/branchCart";

const initialState = {
  currentBrand: null,
  carts: {}, // { brandId: { productId: { product, quantity, options, outletId? } } }
  shippingAddress: {},
  paymentMethod: null,
  total: 0,
  // The outlet (branch) the CURRENT brand's cart is fulfilled from, or null
  // for the main store. Set by the first line added from an outlet storefront
  // and cleared with the cart; every line carries the same id and checkout
  // sends it as the order's fulfilmentOutletId. Replaces a `selectedOutlet`
  // field nothing ever set.
  outletId: null,
  deliverySchedule: null,
};

// The outlet a brand's cart is being ordered from, read off its lines (the
// same reading the restaurant checkout makes), so the slice-level field can
// never disagree with the lines it summarises.
const outletOf = (carts, brandId) =>
  brandId ? cartOutletId(carts[brandId]) : null;

const calculateTotal = (carts, brandId) => {
  const cart = carts[brandId] || {};
  return Object.values(cart).reduce((total, item) => {
    const quantity = Number(item.quantity) || 0;
    const price = Number(item.price) || 0;
    const optionsTotal = (item.options || []).reduce(
      (sum, option) => sum + (Number(option.price) || 0),
      0
    );
    // Per-unit adjustment from buyer-configurable spec picks (e.g.
    // larger dimension that costs +UGX 500). Stored on the line by
    // BrandShopPage; falls back to 0 for legacy carts.
    const specDelta = Number(item.specPriceDelta) || 0;
    return total + (price + optionsTotal + specDelta) * quantity;
  }, 0);
};

/* A cart read back from localStorage is checked again at its branch before
   the shopper pays (4 Oct 2026): prices, stock and a branch's sold out may
   all have moved since it was saved. A basket saved across two branches is
   put on one. */
const loadCartFromLocalStorage = () => {
  const savedCart = localStorage.getItem("brandCart");
  const carts = savedCart ? JSON.parse(savedCart) : {};
  return Object.fromEntries(
    Object.entries(carts || {}).map(([brandId, cart]) => [
      brandId,
      markLinesUnchecked(oneLocationCart(Object.fromEntries(withLineIds(Object.values(cart || {})).map((line) => [line.lineId, line])))),
    ])
  );
};

const loadTotalFromLocalStorage = () => {
  const savedTotal = localStorage.getItem("brandTotal");
  return savedTotal ? JSON.parse(savedTotal) : 0;
};

const saveCartToLocalStorage = (carts) => {
  localStorage.setItem("brandCart", JSON.stringify(carts));
};

const saveTotalToLocalStorage = (total) => {
  localStorage.setItem("brandTotal", JSON.stringify(total));
};

// Persist the current-brand pointer so a full page reload mid-checkout
// doesn't leave carts populated but currentBrand=null.
const loadCurrentBrandFromLocalStorage = () => {
  try {
    return localStorage.getItem("brandCurrentVendor") || null;
  } catch (e) {
    return null;
  }
};

const saveCurrentBrandToLocalStorage = (id) => {
  try {
    if (id) {
      localStorage.setItem("brandCurrentVendor", id);
    } else {
      localStorage.removeItem("brandCurrentVendor");
    }
  } catch (e) { /* storage unavailable — in-memory state still works */ }
};

const buildInitialState = () => {
  const carts = loadCartFromLocalStorage();
  const currentBrand = loadCurrentBrandFromLocalStorage();
  return {
    ...initialState,
    carts,
    total: loadTotalFromLocalStorage(),
    currentBrand,
    outletId: outletOf(carts, currentBrand),
  };
};

const brandCartSlice = createSlice({
  name: "brandCart",
  initialState: buildInitialState(),
  reducers: {
    addToCart: (state, action) => {
      const {
        brandId,
        product,
        quantity,
        options = action.payload.product?.options || [],
        selectedSpecs = action.payload.product?.selectedSpecs || {},
        specPriceDelta = action.payload.product?.specPriceDelta || 0,
      } = action.payload;

      if (!state.carts[brandId]) {
        state.carts[brandId] = {};
      }

      const lineId = cartLineId({ ...product, selectedSpecs, options });

      // Vendor discounts: `price` on the line is salePrice ?? price, with
      // listPrice and the discount kept beside it.
      const isNewLine = !state.carts[brandId][lineId];
      if (isNewLine) {
        state.carts[brandId][lineId] = {
          ...withDealPricing(withoutBranchRead(product)),
          lineId,
          quantity: 0,
          options: [],
          selectedSpecs: {},
          specPriceDelta: 0,
        };
      }

      const productEntry = state.carts[brandId][lineId];
      Object.assign(productEntry, dealPricingFields(product));
      // The outlet this line is fulfilled from. A line added from an outlet
      // storefront names it; one that arrives without (the cart's own rail, a
      // search result) joins the outlet the cart already has, so a basket is
      // never half one branch and half another. A line added to an empty
      // cart from the main store carries none.
      const lineOutletId = product.outletId || outletOf(state.carts, brandId);
      if (lineOutletId) {
        productEntry.outletId = String(lineOutletId);
      } else {
        delete productEntry.outletId;
      }
      // Branch check flags (4 Oct 2026): a product read at this very branch
      // confirms the line at the price just copied onto it; anything else
      // (a new line from search, an older server) is checked before paying.
      const branchFields = lineFieldsFor(product, lineOutletId);
      if (isNewLine || !branchFields.needsCheck) {
        Object.assign(productEntry, branchFields);
      }
      // Ensure quantity is always a valid number
      const newQuantity = Math.max(
        0,
        (Number(productEntry.quantity) || 0) + (Number(quantity) || 0)
      );
      productEntry.quantity = newQuantity;

      // If quantity becomes 0 or negative, remove the item
      if (productEntry.quantity <= 0) {
        delete state.carts[brandId][lineId];
      } else {
        // Update options only if adding (not removing)
        if (quantity > 0) {
          productEntry.options = options;
          // The buyer's variant picks for this product. We replace
          // (rather than merge) on every add so a buyer flipping a
          // dimension and re-adding doesn't carry stale picks.
          if (selectedSpecs && typeof selectedSpecs === "object") {
            productEntry.selectedSpecs = { ...selectedSpecs };
          }
          if (specPriceDelta !== undefined) {
            productEntry.specPriceDelta = Number(specPriceDelta) || 0;
          }
        }
      }

      state.total = calculateTotal(state.carts, brandId);
      state.outletId = outletOf(state.carts, state.currentBrand || brandId);
      saveCartToLocalStorage(state.carts);
      saveTotalToLocalStorage(state.total);
    },

    removeFromCart: (state, action) => {
      const { brandId, productId } = action.payload;
      if (state.carts[brandId] && state.carts[brandId][productId]) {
        delete state.carts[brandId][productId];
      }
      state.total = calculateTotal(state.carts, brandId);
      state.outletId = outletOf(state.carts, state.currentBrand || brandId);
      saveCartToLocalStorage(state.carts);
      saveTotalToLocalStorage(state.total);
    },

    incrementQuantity: (state, action) => {
      const { brandId, productId } = action.payload;
      if (state.carts[brandId] && state.carts[brandId][productId]) {
        const currentQuantity =
          Number(state.carts[brandId][productId].quantity) || 0;
        state.carts[brandId][productId].quantity = currentQuantity + 1;
      }
      state.total = calculateTotal(state.carts, brandId);
      saveCartToLocalStorage(state.carts);
      saveTotalToLocalStorage(state.total);
    },

    decrementQuantity: (state, action) => {
      const { brandId, productId } = action.payload;
      if (state.carts[brandId] && state.carts[brandId][productId]) {
        const currentQuantity =
          Number(state.carts[brandId][productId].quantity) || 0;
        if (currentQuantity > 1) {
          state.carts[brandId][productId].quantity = currentQuantity - 1;
        } else {
          delete state.carts[brandId][productId];
        }
      }
      state.total = calculateTotal(state.carts, brandId);
      state.outletId = outletOf(state.carts, state.currentBrand || brandId);
      saveCartToLocalStorage(state.carts);
      saveTotalToLocalStorage(state.total);
    },

    setCurrentBrand: (state, action) => {
      state.currentBrand = action.payload;
      saveCurrentBrandToLocalStorage(action.payload);
      // Recalculate total and outlet for the new current brand
      state.total = calculateTotal(state.carts, action.payload);
      state.outletId = outletOf(state.carts, action.payload);
      saveTotalToLocalStorage(state.total);
    },

    switchBrand: (state, action) => {
      state.currentBrand = action.payload;
      saveCurrentBrandToLocalStorage(action.payload);
      // Recalculate total and outlet for the new current brand
      state.total = calculateTotal(state.carts, action.payload);
      state.outletId = outletOf(state.carts, action.payload);
      saveTotalToLocalStorage(state.total);
    },

    clearCart: (state, action) => {
      const brandId = action.payload;
      if (state.carts[brandId]) {
        delete state.carts[brandId];
        state.total = calculateTotal(state.carts, brandId);
        state.outletId = outletOf(state.carts, state.currentBrand || brandId);
        saveCartToLocalStorage(state.carts);
        saveTotalToLocalStorage(state.total);
      }
    },

    /* The shopper chose another location of this brand (the location
       picker, "Get it from", or adding from another branch's page). The cart
       follows them instead of being cleared, which made a shopper refill it
       just to order from a nearer branch. Each line is re-stamped with the
       new outlet (`outletId: null` is the brand's main branch, whose lines
       carry none) and marked to be checked there: a branch can have its own
       sold out, on/off and price (4 Oct 2026), so the cart check shows what
       changed before the shopper pays. Quantities and prices stay as they
       are until then. */
    moveCartToOutlet: (state, action) => {
      const { brandId, outletId = null } = action.payload || {};
      const cart = brandId ? state.carts[brandId] : null;
      if (!cart) return;
      state.carts[brandId] = markLinesForMove(cart, outletId);
      state.outletId = outletOf(state.carts, state.currentBrand || brandId);
      saveCartToLocalStorage(state.carts);
    },

    /* The cart check's verdicts (hooks/useBranchCartCheck): lines that are
       fine at the branch are settled, the rest are flagged for the review. */
    applyBranchQuote: (state, action) => {
      const { cartId, verdicts } = action.payload || {};
      const cart = cartId ? state.carts[cartId] : null;
      if (!cart) return;
      const next = applyQuote(cart, verdicts);
      if (next === cart) return;
      state.carts[cartId] = next;
      saveCartToLocalStorage(state.carts);
    },

    /* "Keep at new price" in the review: the line takes the branch price. */
    acceptBranchPrice: (state, action) => {
      const { cartId, productId } = action.payload || {};
      const cart = cartId ? state.carts[cartId] : null;
      if (!cart) return;
      state.carts[cartId] = acceptQuotedPrice(cart, productId);
      state.total = calculateTotal(state.carts, state.currentBrand || cartId);
      saveCartToLocalStorage(state.carts);
      saveTotalToLocalStorage(state.total);
    },

    resetBrandCart: (state) => {
      state.currentBrand = null;
      saveCurrentBrandToLocalStorage(null);
      state.carts = {};
      state.shippingAddress = {};
      state.paymentMethod = null;
      state.total = 0;
      state.outletId = null;
      state.deliverySchedule = null;
      localStorage.removeItem("brandCart");
      localStorage.removeItem("brandTotal");
    },

    saveShippingAddress: (state, action) => {
      state.shippingAddress = action.payload;
    },

    savePaymentMethod: (state, action) => {
      state.paymentMethod = action.payload;
    },

    setDeliverySchedule: (state, action) => {
      state.deliverySchedule = action.payload;
    },
  },
  extraReducers: (builder) => {
    /* A branch changed one product live (catalog:public): the lines it
       touches are checked again before Place order. Product ids are unique
       across vendors, so every brand cart can be offered the event. */
    builder.addCase(branchProductChanged, (state, action) => {
      let changed = false;
      Object.keys(state.carts).forEach((brandId) => {
        const cart = state.carts[brandId];
        const next = markLinesChanged(cart, action.payload || {});
        if (next !== cart) {
          state.carts[brandId] = next;
          changed = true;
        }
      });
      if (changed) saveCartToLocalStorage(state.carts);
    });
  },
});

export const {
  addToCart,
  removeFromCart,
  incrementQuantity,
  decrementQuantity,
  setCurrentBrand,
  switchBrand,
  clearCart,
  moveCartToOutlet,
  applyBranchQuote,
  acceptBranchPrice,
  resetBrandCart,
  saveShippingAddress,
  savePaymentMethod,
  setDeliverySchedule,
} = brandCartSlice.actions;

export default brandCartSlice.reducer;
